import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Snackbar,
  Alert,
  Typography
} from '@mui/material';
import { responsiveSpacing } from '../../themes/responsive';
import { theme } from '../../themes/technicalAlerts';
import TechnicalAlertsTable from '../../components/TechnicalAlertsTable/TechnicalAlertsTable';
import KPIBox from '../../components/shared/KPIBox';

// Overview Tab Component (main content)
export const DiagnosticsOverviewTab = () => {
  const { t } = useTranslation();
  
  // Filter state for server-side filtering - initialize empty, will be set by table component
  const [tableFilters, setTableFilters] = React.useState({});
  const [tableFilterData, setTableFilterData] = React.useState(null);
  const [tableSorter, setTableSorter] = React.useState(null);
  
  // Totals state for KPI boxes
  const [totals, setTotals] = React.useState(null);
  
  // Toast notification state
  const [toast, setToast] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Toast helper functions
  const showToast = React.useCallback((message, severity = 'success') => {
    setToast({ open: true, message, severity });
  }, []);

  const hideToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  // Handle table filter changes (called by TechnicalAlertsTable component)
  const handleFiltersChange = React.useCallback((filters, filterData, sorter) => {
    setTableFilters(filters);
    setTableFilterData(filterData);
    setTableSorter(sorter);
  }, []);

  // Handle dashboard filter clicks (from AlertDashboard component)
  const handleDashboardFilterChange = React.useCallback((filters) => {
    setTableFilters(filters);
    // Notify table component of filter change
    handleFiltersChange(filters, tableFilterData, tableSorter);
  }, [tableFilterData, tableSorter, handleFiltersChange]);

  // Handle totals updates from table component
  const handleTotalsChange = React.useCallback((newTotals) => {
    setTotals(newTotals);
  }, []);

  // Update global sidebar (empty for now)
  React.useEffect(() => {
    window.currentPageSidebar = null;
    window.dispatchEvent(new CustomEvent('sidebarUpdate'));
  }, []);

  // Clear sidebar when component unmounts
  React.useEffect(() => {
    return () => {
      window.currentPageSidebar = null;
      window.dispatchEvent(new CustomEvent('sidebarUpdate'));
    };
  }, []);

  return (
    <Box sx={{ 
      px: 0,
      py: responsiveSpacing.md,
      width: '100%',
      maxWidth: '100vw',
      boxSizing: 'border-box'
    }}>

      {/* Technical Alerts Table */}
      <Box sx={{ px: responsiveSpacing.xs }}>
        <TechnicalAlertsTable
          onFiltersChange={handleFiltersChange}
          onTotalsChange={handleTotalsChange}
          showToast={showToast}
        />
      </Box>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={hideToast} 
          severity={toast.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            ...theme.typography.body1
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Default export for the main page
const DiagnosticsOverview = () => {
  return <DiagnosticsOverviewTab />;
};

export default DiagnosticsOverview;